import { pgTable, text, timestamp, boolean, integer, uuid, jsonb, decimal } from 'drizzle-orm/pg-core';
import { createInsertSchema } from 'drizzle-zod';
import { z } from 'zod';
import { relations } from 'drizzle-orm';

// Businesses table
export const businesses = pgTable('businesses', {
  id: uuid('id').defaultRandom().primaryKey(),
  name: text('name').notNull(),
  description: text('description'),
  category: text('category').notNull(),
  address: text('address').notNull(),
  city: text('city').notNull(),
  state: text('state').notNull(),
  zipCode: text('zip_code').notNull(),
  phone: text('phone'),
  email: text('email'),
  website: text('website'),
  latitude: decimal('latitude', { precision: 10, scale: 7 }),
  longitude: decimal('longitude', { precision: 10, scale: 7 }),
  images: text('images').array().default([]),
  hours: text('hours'),
  approved: boolean('approved').default(false),
  isFBAOwned: boolean('is_fba_owned').default(true),
  businessType: text('business_type').default('established'),
  paymentMethods: text('payment_methods').array().default([]),
  isMobileService: boolean('is_mobile_service').default(false),
  priceRange: text('price_range'),
  socialMedia: jsonb('social_media').default({}),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow()
});

// Users table
export const users = pgTable('users', {
  id: uuid('id').defaultRandom().primaryKey(),
  email: text('email').notNull().unique(),
  name: text('name').notNull(),
  phone: text('phone'),
  savedBusinesses: text('saved_businesses').array().default([]),
  isAdmin: boolean('is_admin').default(false),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow()
});

// Reviews table
export const reviews = pgTable('reviews', {
  id: uuid('id').defaultRandom().primaryKey(),
  businessId: uuid('business_id').notNull().references(() => businesses.id, { onDelete: 'cascade' }),
  userId: uuid('user_id').notNull().references(() => users.id, { onDelete: 'cascade' }),
  rating: integer('rating').notNull(),
  comment: text('comment'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow()
});

// Job postings table
export const jobPostings = pgTable('job_postings', {
  id: uuid('id').defaultRandom().primaryKey(),
  businessId: uuid('business_id').notNull().references(() => businesses.id, { onDelete: 'cascade' }),
  title: text('title').notNull(),
  description: text('description').notNull(),
  requirements: text('requirements').array().default([]),
  salary: text('salary'),
  type: text('type').notNull(),
  location: text('location').notNull(),
  contactEmail: text('contact_email').notNull(),
  contactPhone: text('contact_phone'),
  isFBAPreferred: boolean('is_fba_preferred').default(false),
  isYouthFriendly: boolean('is_youth_friendly').default(false),
  active: boolean('active').default(true),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow()
});

// Scraped businesses table
export const scrapedBusinesses = pgTable('scraped_businesses', {
  id: uuid('id').defaultRandom().primaryKey(),
  name: text('name').notNull(),
  category: text('category'),
  address: text('address'),
  city: text('city'),
  state: text('state'),
  phone: text('phone'),
  website: text('website'),
  source: text('source').notNull(),
  sourceUrl: text('source_url'),
  verified: boolean('verified').default(false),
  scrapedAt: timestamp('scraped_at').defaultNow(),
  promotedToBusinessId: uuid('promoted_to_business_id').references(() => businesses.id),
  rawData: jsonb('raw_data')
});

// Notifications table for business submissions
export const notifications = pgTable('notifications', {
  id: uuid('id').defaultRandom().primaryKey(),
  type: text('type').notNull(), // 'business_submission', 'business_approved', etc.
  businessId: uuid('business_id').references(() => businesses.id),
  userId: uuid('user_id').references(() => users.id),
  message: text('message').notNull(),
  phoneNumber: text('phone_number'),
  emailAddress: text('email_address'),
  sent: boolean('sent').default(false),
  sentAt: timestamp('sent_at'),
  createdAt: timestamp('created_at').defaultNow()
});

// Define relations
export const businessesRelations = relations(businesses, ({ many }) => ({
  reviews: many(reviews),
  jobPostings: many(jobPostings),
  notifications: many(notifications)
}));

export const usersRelations = relations(users, ({ many }) => ({
  reviews: many(reviews),
  notifications: many(notifications)
}));

export const reviewsRelations = relations(reviews, ({ one }) => ({
  business: one(businesses, {
    fields: [reviews.businessId],
    references: [businesses.id]
  }),
  user: one(users, {
    fields: [reviews.userId],
    references: [users.id]
  })
}));

export const jobPostingsRelations = relations(jobPostings, ({ one }) => ({
  business: one(businesses, {
    fields: [jobPostings.businessId],
    references: [businesses.id]
  })
}));

export const notificationsRelations = relations(notifications, ({ one }) => ({
  business: one(businesses, {
    fields: [notifications.businessId],
    references: [businesses.id]
  }),
  user: one(users, {
    fields: [notifications.userId],
    references: [users.id]
  })
}));

// Create insert schemas
export const businessInsertSchema = createInsertSchema(businesses).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

export const userInsertSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

export const reviewInsertSchema = createInsertSchema(reviews).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

export const jobPostingInsertSchema = createInsertSchema(jobPostings).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

export const notificationInsertSchema = createInsertSchema(notifications).omit({
  id: true,
  sentAt: true,
  createdAt: true
});

// Export types
export type Business = typeof businesses.$inferSelect;
export type BusinessInsert = z.infer<typeof businessInsertSchema>;
export type User = typeof users.$inferSelect;
export type UserInsert = z.infer<typeof userInsertSchema>;
export type Review = typeof reviews.$inferSelect;
export type ReviewInsert = z.infer<typeof reviewInsertSchema>;
export type JobPosting = typeof jobPostings.$inferSelect;
export type JobPostingInsert = z.infer<typeof jobPostingInsertSchema>;
export type ScrapedBusiness = typeof scrapedBusinesses.$inferSelect;
export type Notification = typeof notifications.$inferSelect;
export type NotificationInsert = z.infer<typeof notificationInsertSchema>;

// Business categories
export const businessCategories = [
  'Restaurant',
  'Beauty & Wellness',
  'Professional Services',
  'Retail',
  'Health & Medical',
  'Automotive',
  'Real Estate',
  'Technology',
  'Education',
  'Entertainment',
  'Home Services',
  'Financial Services',
  'Legal Services',
  'Non-Profit',
  'Youth Services',
  'Other'
] as const;